/**
 * Analytics hooks for DocuMind Summit 2026.
 *
 * useFlowStep(step)
 *   Fires a flow_step event once when the component mounts.
 *   Drop into each main page (HomePage, ProcessPage, ReviewPage, ReportPage).
 *
 * useSectionTimer(page, section)
 *   Starts a timer on mount, fires a time_on_section event on unmount.
 *   Drop into any tab panel or section component to capture heatmap data.
 *
 * useModuleVisit(module)
 *   Fires a module_visit event once when the component mounts.
 *   Drop into deep-dive pages (GuardrailsShowcase, StrandsAgentsPage, etc.)
 */

import { useEffect, useRef } from 'react';
import analyticsService from '../services/analyticsService';

/**
 * Track a main demo flow step on mount.
 * @param {string} step - 'home' | 'process' | 'review' | 'report'
 */
export function useFlowStep(step) {
  const fired = useRef(false);
  useEffect(() => {
    if (!fired.current) {
      fired.current = true;
      analyticsService.trackFlowStep(step);
    }
  }, []); // fires once on mount
}

/**
 * Track time spent on a section. Fires on unmount (or tab change).
 * @param {string} page    - page name, e.g. 'review'
 * @param {string} section - section name, e.g. 'citations'
 */
export function useSectionTimer(page, section) {
  const startRef = useRef(Date.now());

  useEffect(() => {
    startRef.current = Date.now();
    return () => {
      const durationMs = Date.now() - startRef.current;
      analyticsService.trackTimeOnSection(page, section, durationMs);
    };
  }, []); // fires on mount/unmount only
}

/**
 * Track a visit to an optional deep-dive module on mount.
 * @param {string} module - e.g. 'guardrails', 'strands_agents', 'architecture'
 */
export function useModuleVisit(module) {
  const fired = useRef(false);
  useEffect(() => {
    if (!fired.current) {
      fired.current = true;
      analyticsService.trackModuleVisit(module);
    }
  }, []); // fires once on mount
}
